import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import CategoryPill from '../CategoryPill/CategoryPill';
import formStyles from '../FormModal/FormModal.module.css';
import styles from './PreviewModal.module.css';

/**
 * PreviewModal — resizable / draggable file preview with a metadata
 * sidebar. Ported from Themis (`cybpass-themis-agent/frontend/src/
 * components/ui/Modals/PreviewModal.jsx`); the preview/render logic is
 * kept verbatim so future Themis changes are cheap to pull. Two Athena
 * adaptations:
 *   - Themis `Capsule` → Athena's shared `CategoryPill` (neutral tone).
 *   - Themis `complianceService.getEvidencePreviewPdf()` → the injected
 *     `fetchServerPdf` prop, so the component stays decoupled from any
 *     specific backend service. When omitted, legacy Office formats
 *     (.doc/.ppt/.pptx) fall back to the download tile.
 * The CSS chrome was retargeted onto Athena's theme-flipping tokens;
 * the rendered-document canvas stays light ("paper") in both themes.
 *
 * Props:
 *   file          — evidence/file object (see field resolution below)
 *   tenantId      — passed through to fetchServerPdf
 *   onClose       — close handler (Escape / backdrop / X)
 *   onDownload    — (fileId) => void; download button handler
 *   fetchServerPdf — optional (fileId, tenantId) => Promise<{ content }>;
 *                    server-side PDF conversion for .doc/.ppt/.pptx
 */

const MIN_WIDTH = 500;
const MIN_HEIGHT = 400;

// Extensions that require server-side LibreOffice conversion to PDF
const SERVER_CONVERTIBLE = new Set(['doc', 'ppt', 'pptx']);

// Decode base64 to a Uint8Array (used by docx/xlsx libraries)
const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const PreviewModal = ({ file, tenantId, onClose, onDownload, fetchServerPdf = null }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [decodedContent, setDecodedContent] = useState(null);
  const [docxHtml, setDocxHtml] = useState(null);
  const [xlsxSheets, setXlsxSheets] = useState(null); // [{ name, html }]
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const [serverPdfBase64, setServerPdfBase64] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  // null = not yet resized, still using default centered flex layout.
  // Once a resize starts we snapshot left/top and switch to explicit
  // positioning so only the dragged edge moves.
  const [box, setBox] = useState(null);
  const containerRef = useRef(null);

  const handleResizeStart = useCallback((e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const startW = rect.width;
    const startH = rect.height;

    const CURSOR_FOR = {
      e: 'ew-resize', w: 'ew-resize',
      n: 'ns-resize', s: 'ns-resize',
      ne: 'nesw-resize', sw: 'nesw-resize',
      nw: 'nwse-resize', se: 'nwse-resize',
    };

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;

      // Anchor the opposite edge — only the dragged side moves.
      let left = startLeft;
      let top = startTop;
      let width = startW;
      let height = startH;

      if (direction.includes('e')) {
        width = Math.max(MIN_WIDTH, startW + dx);
      } else if (direction.includes('w')) {
        width = Math.max(MIN_WIDTH, startW - dx);
        left = startLeft + (startW - width); // keep right edge fixed
      }

      if (direction.includes('s')) {
        height = Math.max(MIN_HEIGHT, startH + dy);
      } else if (direction.includes('n')) {
        height = Math.max(MIN_HEIGHT, startH - dy);
        top = startTop + (startH - height); // keep bottom edge fixed
      }

      setBox({ left, top, width, height });
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = CURSOR_FOR[direction] || 'default';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Drag the modal by its header
  const handleHeaderMouseDown = useCallback((e) => {
    // Don't start dragging when the click began on a button (close/download)
    if (e.target.closest('button')) return;
    const el = containerRef.current;
    if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = rect.left;
    const startTop = rect.top;
    const width = rect.width;
    const height = rect.height;

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setBox({ left: startLeft + dx, top: startTop + dy, width, height });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  const metadata = file.evidence_metadata || file;
  const fileName = metadata.evidence_name || file.fileName;
  const fileSize = metadata.evidence_size || file.fileSize;
  const fileType = metadata.evidence_type || file.fileType;
  // Real file extension (e.g. '.docx') — used to dispatch to the correct
  // preview branch. The backend `evidence_type` is a category enum (DOC/EXCEL/...)
  // and maps multiple real extensions to the same value, so we can't rely on it.
  const rawExt = metadata.file_extension || file.fileExtension || '';
  const fileExt = rawExt.startsWith('.') ? rawExt.slice(1).toLowerCase() : rawExt.toLowerCase();
  const source = metadata.evidence_source || file.source;
  const uploadedBy = metadata.uploaded_by || file.uploadedBy;
  const approvedBy = metadata.approved_by || file.approvedBy;
  const lastUpdate = metadata.last_uploaded_date || file.lastUpdate;
  const description = metadata.evidence_description || file.description;
  const relatedControls = metadata.related_controls || (file.relatedControl ? [file.relatedControl] : []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setIsLoading(true);
      setPreviewError(null);
      setDocxHtml(null);
      setXlsxSheets(null);
      setServerPdfBase64(null);
      setDecodedContent(null);

      const lower = fileExt;

      // Legacy Office formats → fetch converted PDF from backend
      if (SERVER_CONVERTIBLE.has(lower)) {
        if (!fetchServerPdf) {
          if (!cancelled) {
            setPreviewError('Preview unavailable');
            setIsLoading(false);
          }
          return;
        }
        try {
          const resp = await fetchServerPdf(file.id, tenantId);
          if (!cancelled) setServerPdfBase64(resp.content);
        } catch (err) {
          if (!cancelled) {
            console.error('Preview conversion failed:', err);
            setPreviewError(err?.response?.data?.detail || 'Preview unavailable');
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      const hasBase64 = file.evidence_file?.length > 0;
      if (!hasBase64) { setIsLoading(false); return; }

      try {
        // DOCX → HTML via mammoth
        if (lower === 'docx') {
          const bytes = base64ToBytes(file.evidence_file);
          const { value } = await mammoth.convertToHtml({ arrayBuffer: bytes.buffer });
          if (!cancelled) setDocxHtml(value);
        }
        // XLSX / XLS → HTML tables via SheetJS
        else if (lower === 'xlsx' || lower === 'xls') {
          const bytes = base64ToBytes(file.evidence_file);
          const wb = XLSX.read(bytes, { type: 'array' });
          const sheets = wb.SheetNames.map((name) => ({
            name,
            html: XLSX.utils.sheet_to_html(wb.Sheets[name]),
          }));
          if (!cancelled) {
            setXlsxSheets(sheets);
            setActiveSheetIdx(0);
          }
        }
        // Text-based formats → decode as UTF-8
        else {
          const bytes = base64ToBytes(file.evidence_file);
          const decoded = new TextDecoder('utf-8').decode(bytes);
          if (!cancelled && decoded.length > 0) setDecodedContent(decoded);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to decode evidence file:', err);
          setPreviewError('Failed to load preview');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.evidence_file, file.evidence_file_type, file.id, tenantId, fileExt]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFileTypeIcon = (type) => (
    <svg viewBox="0 0 24 24">
      <path
        d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
        fill="currentColor"
      />
    </svg>
  );

  const getPreviewContent = () => {
    const lower = fileExt;
    const mimeType = file.evidence_file_type || '';
    const hasBase64 = file.evidence_file?.length > 0;

    // Server-converted PDF (for .doc / .ppt / .pptx)
    if (serverPdfBase64) {
      return (
        <div className={styles.pdfPreview}>
          <iframe
            src={`data:application/pdf;base64,${serverPdfBase64}`}
            className={styles.previewIframe}
            title={`Preview of ${fileName}`}
          />
        </div>
      );
    }

    // DOCX — rendered HTML from mammoth
    if (docxHtml !== null) {
      return (
        <div className={styles.docxPreview}>
          <div
            className={styles.docxContent}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: docxHtml }}
          />
        </div>
      );
    }

    // XLSX / XLS — tab bar + sheet table
    if (xlsxSheets && xlsxSheets.length > 0) {
      const active = xlsxSheets[activeSheetIdx];
      return (
        <div className={styles.xlsxPreview}>
          {xlsxSheets.length > 1 && (
            <div className={styles.xlsxTabs}>
              {xlsxSheets.map((s, i) => (
                <button
                  key={s.name}
                  type="button"
                  className={`${styles.xlsxTab} ${i === activeSheetIdx ? styles.xlsxTabActive : ''}`}
                  onClick={() => setActiveSheetIdx(i)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <div
            className={styles.xlsxSheet}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: active.html }}
          />
        </div>
      );
    }

    // Markdown
    if (
      hasBase64 && decodedContent &&
      (mimeType.includes('markdown') || lower === 'markdown' || lower === 'md')
    ) {
      return (
        <div className={styles.markdownPreview}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{decodedContent}</ReactMarkdown>
        </div>
      );
    }

    // Plain text
    if (
      hasBase64 && decodedContent &&
      (mimeType.includes('text/plain') || lower === 'txt')
    ) {
      return (
        <div className={styles.markdownPreview}>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{decodedContent}</pre>
        </div>
      );
    }

    // Images (including SVG) — use base64 data URI, fall back to file.url
    if (
      ['png', 'jpg', 'jpeg', 'gif', 'svg', 'image'].includes(lower) ||
      mimeType.startsWith('image/')
    ) {
      const mime = lower === 'svg' ? 'image/svg+xml' : (mimeType || 'image/png');
      const src = hasBase64 ? `data:${mime};base64,${file.evidence_file}` : file.url;
      if (src) {
        return (
          <div className={styles.imagePreview}>
            <img src={src} alt={fileName} className={styles.previewImage} />
          </div>
        );
      }
    }

    // PDF — use base64 data URI, fall back to file.url
    if (lower === 'pdf' || mimeType === 'application/pdf') {
      const src = hasBase64
        ? `data:application/pdf;base64,${file.evidence_file}`
        : file.url;

      if (src) {
        return (
          <div className={styles.pdfPreview}>
            <iframe
              src={src}
              className={styles.previewIframe}
              title={`Preview of ${fileName}`}
            />
          </div>
        );
      }
    }

    // Preview error from server conversion (LibreOffice missing, timeout, etc.)
    if (previewError) {
      return (
        <div className={styles.fileFallback}>
          <div className={styles.fallbackIcon}>{getFileTypeIcon(lower)}</div>
          <h3 className={styles.fallbackTitle}>{fileName}</h3>
          <p className={styles.fallbackType}>{fileType.toUpperCase()} Document</p>
          <p className={styles.fallbackHint}>{previewError}</p>
          <button
            type="button"
            className={styles.downloadButton}
            onClick={() => onDownload?.(file.id)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" fill="currentColor" />
            </svg>
            Download File
          </button>
        </div>
      );
    }

    return (
      <div className={styles.fileFallback}>
        <div className={styles.fallbackIcon}>{getFileTypeIcon(lower)}</div>
        <h3 className={styles.fallbackTitle}>{fileName}</h3>
        <p className={styles.fallbackType}>{fileType.toUpperCase()} Document</p>
        <p className={styles.fallbackHint}>
          This file type cannot be previewed directly.
        </p>
        <button
          type="button"
          className={styles.downloadButton}
          onClick={() => onDownload?.(file.id)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" fill="currentColor" />
          </svg>
          Download File
        </button>
      </div>
    );
  };

  return (
    <div className={formStyles.modalOverlay} onClick={handleBackdropClick}>
      <div
        ref={containerRef}
        className={`${formStyles.modalContainer} ${styles.resizable} ${box ? styles.positioned : ''}`}
        style={
          box
            ? {
                position: 'fixed',
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
                maxWidth: 'none',
                maxHeight: 'none',
                margin: 0,
              }
            : { width: '80vw', height: '80vh', maxWidth: '90vw', maxHeight: '90vh' }
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-modal-title"
      >
        {/* Resize handles — 4 edges + 4 corners */}
        <div className={styles.resizeN}  onMouseDown={(e) => handleResizeStart(e, 'n')} />
        <div className={styles.resizeS}  onMouseDown={(e) => handleResizeStart(e, 's')} />
        <div className={styles.resizeE}  onMouseDown={(e) => handleResizeStart(e, 'e')} />
        <div className={styles.resizeW}  onMouseDown={(e) => handleResizeStart(e, 'w')} />
        <div className={styles.resizeNE} onMouseDown={(e) => handleResizeStart(e, 'ne')} />
        <div className={styles.resizeNW} onMouseDown={(e) => handleResizeStart(e, 'nw')} />
        <div className={styles.resizeSE} onMouseDown={(e) => handleResizeStart(e, 'se')} />
        <div className={styles.resizeSW} onMouseDown={(e) => handleResizeStart(e, 'sw')} />
        {/* Header — reuses FormModal chrome */}
        <div
          className={`${formStyles.modalHeader} ${styles.header}`}
          onMouseDown={handleHeaderMouseDown}
          style={{ cursor: 'grab' }}
        >
          <div className={styles.headerLeft}>
            <div className={styles.fileIcon}>{getFileTypeIcon(fileType)}</div>
            <div className={styles.fileMeta}>
              <h2 id="preview-modal-title" className={formStyles.modalTitle}>
                {fileName}
              </h2>
              <div className={styles.metaRow}>
                <span>{fileSize}</span>
                <span className={styles.dot}>·</span>
                <span>{fileType.toUpperCase()}</span>
                <span className={styles.dot}>·</span>
                <span>Updated {formatDate(lastUpdate)}</span>
              </div>
            </div>
          </div>

          <div className={styles.headerRight}>
            <button
              type="button"
              className={formStyles.closeButton}
              onClick={() => onDownload?.(file.id)}
              title="Download"
            >
              <svg viewBox="0 0 24 24" className={formStyles.closeIcon} aria-hidden="true">
                <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" fill="currentColor" />
              </svg>
            </button>
            <button
              type="button"
              className={formStyles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" className={formStyles.closeIcon} aria-hidden="true">
                <path
                  d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body — preview + sidebar (no form, just content) */}
        <div className={styles.body}>
          <div className={styles.previewPane}>
            {isLoading ? (
              <div className={styles.loading}>
                <span className={formStyles.loadingSpinner} />
                <p>Loading preview…</p>
              </div>
            ) : (
              getPreviewContent()
            )}
          </div>

          <div className={styles.sidebar}>
            {/* Details */}
            <section className={styles.section}>
              <h4 className={styles.sectionTitle}>File Details</h4>
              <dl className={styles.detailList}>
                <div className={styles.detailRow}>
                  <dt>Source</dt>
                  <dd>{source}</dd>
                </div>
                <div className={styles.detailRow}>
                  <dt>Uploaded By</dt>
                  <dd>{uploadedBy}</dd>
                </div>
                {approvedBy && (
                  <div className={styles.detailRow}>
                    <dt>Approved By</dt>
                    <dd>{approvedBy}</dd>
                  </div>
                )}
              </dl>
            </section>

            {/* Related Controls */}
            {relatedControls?.length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Related Controls</h4>
                <div className={styles.capsuleWrap}>
                  {relatedControls.map((ctrl, i) => (
                    <CategoryPill key={i} label={ctrl} />
                  ))}
                </div>
              </section>
            )}

            {/* Description */}
            {description && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Description</h4>
                <p className={styles.descriptionText}>{description}</p>
              </section>
            )}

            {/* Tags */}
            {file.tags?.length > 0 && (
              <section className={styles.section}>
                <h4 className={styles.sectionTitle}>Tags</h4>
                <div className={styles.capsuleWrap}>
                  {file.tags.map((tag, i) => (
                    <CategoryPill key={i} label={tag} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
